// src/pages/Home.jsx
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const YELLOW = '#FFED29';
const BLACK = '#1a1a1a';
const DARK_GRAY = '#2d2d2d';
const WHITE = '#ffffff';

export default function Home() {
  const aboutRef = useRef(null);
  const highlightsRef = useRef(null);
  const galleryRef = useRef(null);

  // ---- TikTok Pixel Code ----
  useEffect(() => {
    // Check if TikTok pixel is already loaded
    if (window.ttq) return;

    const t = 'ttq';
    window.TiktokAnalyticsObject = t;
    const ttq = window[t] = window[t] || [];
    
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
    
    ttq.setAndDefer = function (target, method) {
      target[method] = function () {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    
    ttq.instance = function (id) {
      const instance = ttq._i[id] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(instance, ttq.methods[n]);
      }
      return instance;
    };
    
    ttq.load = function (pixelId, options) {
      const scriptUrl = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[pixelId] = [];
      ttq._i[pixelId]._u = scriptUrl;
      ttq._t = ttq._t || {};
      ttq._t[pixelId] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[pixelId] = options || {};
      
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = scriptUrl + "?sdkid=" + pixelId + "&lib=" + t;
      const firstScript = document.getElementsByTagName("script")[0];
      firstScript.parentNode.insertBefore(script, firstScript);
    };

    ttq.load('D4E3333C77UF4SPAJ6OG');
    ttq.page();
  }, []);

  // ---- Mobile viewport fix ----
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // ---- Intersection animations ----
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        }),
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    [aboutRef, highlightsRef, galleryRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () =>
      [aboutRef, highlightsRef, galleryRef].forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
  }, []);

  const highlights = [
    { title: 'Eigener Montageplatz', icon: 'workspace', desc: 'Professionelle Arbeitsplätze mit allem, was du brauchst' },
    { title: 'Hebebühne & Werkzeug', icon: 'tools', desc: 'Modernste Ausrüstung für jede Reparatur' },
    { title: 'Saubere Umgebung', icon: 'clean', desc: 'Gepflegte Halle mit industriellem Standard' },
    { title: 'Flexible Buchung', icon: 'time', desc: 'Stunden-, Tages- oder Wochenmiete möglich' },
    { title: 'Zulassungsdienst', icon: 'badge-check', desc: 'Wir übernehmen Anmeldung, Abmeldung und Kennzeichenservice – schnell und unkompliziert.' },
    { title: 'Innen- & Außenaufbereitung', icon: 'workspace', desc: 'Professionelle Fahrzeugpflege für ein sauberes, glänzendes Ergebnis – innen wie außen.' },
  ];

  // ---- 6 gallery images (put these in /public) ----
  const galleryImages = [
    { src: '/1 (1).jpeg', title: 'Vor dem Tor', desc: 'Fahrzeuge bereit zur Annahme', span2: true, tall: true },
    { src: '/1 (2).jpeg', title: 'Einfahrt & Hof', desc: 'Breite Zufahrt – genug Platz', span2: false, tall: true },
    { src: '/1 (3).jpeg', title: 'Reifenservice', desc: 'Montage & Wuchten vor Ort', span2: false, tall: false },
    { src: '/1 (4).jpeg', title: 'Hebebühnen', desc: 'Ordentliche, helle Arbeitsplätze', span2: true, tall: false },
    { src: '/1 (5).jpeg', title: 'Fahrzeugbestand', desc: 'Sichtprüfung & Übergabe', span2: false, tall: false },
    { src: '/1 (6).jpeg', title: 'Startklar nach dem Service', desc: 'Geprüft, sauber & fahrbereit', span2: false, tall: false },
  ];

  return (
    <>
      <style>{`
        :root { --vh: 1vh; --nav-h: 64px; } /* set --nav-h to your fixed header height */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif; background: ${BLACK}; }
        .animate-in { opacity: 1 !important; transform: translateY(0) !important; }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }

        /* ---------- HERO ---------- */
        .hero {
          position: relative;
          /* Instead of fixed 100vh, allow space for header and small screens */
          min-height: calc(var(--vh, 1vh) * 100 - var(--nav-h));
          padding: calc(var(--nav-h) + 20px) 20px 28px; /* clear the fixed header */
          background: ${BLACK};
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: visible; /* allow content to grow on small phones */
        }
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr;   /* mobile first */
          gap: 24px;
          width: 100%;
          max-width: 1200px;
          align-items: center;
        }
        @media (min-width: 1024px) {
          .hero-grid { grid-template-columns: 1.2fr 0.8fr; gap: 60px; }
        }

        .hero-info {
          display: grid;
          gap: 16px;
          width: 100%;
          justify-self: stretch;   /* full width on mobile */
          max-width: 100%;
        }
        @media (min-width: 1024px) {
          .hero-info { max-width: 420px; justify-self: end; }
        }

        .lg-br { display: none; }
        @media (min-width: 1024px) { .lg-br { display: inline; } }

        /* ---------- ABOUT / HIGHLIGHTS ---------- */
        .about-content { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
        @media (max-width: 1024px) {
          .about-content { grid-template-columns: 1fr; gap: 36px; }
          .highlights-grid { grid-template-columns: 1fr !important; }
        }

        /* ---------- GALLERY ---------- */
        .gallery-grid { display: grid; gap: 12px; grid-template-columns: 1fr; }
        .gallery-item { position: relative; overflow: hidden; border-radius: 10px; }
        .gallery-item .img { width: 100%; height: 240px; object-fit: cover; transition: transform .5s ease; cursor: pointer; }
        .gallery-item:hover .img { transform: scale(1.05); }
        .gallery-caption { position: absolute; inset-inline: 0; bottom: 0; color: ${WHITE}; background: linear-gradient(to top, rgba(0,0,0,.8), rgba(0,0,0,0)); padding: 18px 16px 12px; }
        .span-2 { grid-column: span 1; }
        .h-320 .img { height: 320px; }
        .h-280 .img { height: 280px; }
        @media (min-width: 640px) { .gallery-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) {
          .gallery-grid { grid-template-columns: repeat(3, 1fr); }
          .span-2 { grid-column: span 2; }
        }

        /* View Gallery Button */
        .view-gallery-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          background: ${YELLOW};
          color: ${BLACK};
          text-decoration: none;
          font-size: 1rem;
          font-weight: 800;
          border-radius: 8px;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 30px;
          box-shadow: 0 4px 15px rgba(255, 237, 41, 0.3);
        }

        .view-gallery-btn:hover {
          background: #FFE600;
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(255, 237, 41, 0.5);
        }

        .view-gallery-btn svg {
          transition: transform 0.3s ease;
        }

        .view-gallery-btn:hover svg {
          transform: translateX(4px);
        }

        .gallery-header-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          flex-wrap: wrap;
          gap: 20px;
        }

        @media (max-width: 768px) {
          .gallery-header-section {
            flex-direction: column;
          }
          
          .view-gallery-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      <div style={{ backgroundColor: BLACK, color: WHITE }}>
        {/* ---------- HERO ---------- */}
        <section className="hero">
          {/* Background overlay image in /public */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: 'url("/Selection (4).png")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.25,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
          <div className="hero-grid" style={{ position: 'relative', zIndex: 2 }}>
            {/* Left */}
            <div style={{ animation: 'slideInLeft 0.8s ease-out' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: YELLOW, marginBottom: 14, letterSpacing: '3px', textTransform: 'uppercase' }}>
                Willkommen bei
              </div>
              <h1
                className="hero-title"
                style={{
                  fontSize: 'clamp(2rem, 8vw, 5rem)',
                  fontWeight: 900,
                  color: WHITE,
                  marginBottom: 18,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  wordBreak: 'break-word',
                }}
              >
                AUTO <span className="lg-br"><br /></span>
                HOBBY <span className="lg-br"><br /></span>
                <span style={{ color: YELLOW }}>WERKSTATT</span>
              </h1>
              <p
                style={{
                  fontSize: 'clamp(0.98rem, 2.6vw, 1.25rem)',
                  color: 'rgba(255,255,255,0.78)',
                  marginBottom: 20,
                  lineHeight: 1.6,
                  maxWidth: 560,
                }}
              >
                Der perfekte Ort für alle Schrauber und Auto-Enthusiasten. Professionelle Ausstattung, faire Preise,
                flexible Buchung.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link
                  to="/kontakt"
                  style={{
                    display: 'inline-block',
                    padding: '14px 22px',
                    background: YELLOW,
                    color: BLACK,
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 800,
                    borderRadius: 6,
                    transition: 'all 0.25s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FFE600'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = YELLOW; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  Kontakt
                </Link>
                <Link
                  to="/leistungen"
                  style={{
                    display: 'inline-block',
                    padding: '14px 22px',
                    background: 'transparent',
                    color: WHITE,
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 800,
                    borderRadius: 6,
                    transition: 'all 0.25s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    border: `2px solid ${WHITE}`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = WHITE; e.currentTarget.style.color = BLACK; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = WHITE; }}
                >
                  Unsere&nbsp;Leistungen
                </Link>
              </div>
            </div>

            {/* Right */}
            <div className="hero-info" style={{ animation: 'slideInRight 0.8s ease-out' }}>
              <div style={{ background: DARK_GRAY, padding: 22, borderLeft: `4px solid ${YELLOW}`, borderRadius: 8 }}>
                <div style={{ fontSize: 'clamp(1.8rem, 9vw, 3rem)', fontWeight: 'bold', color: YELLOW, marginBottom: 6 }}>24/7</div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Flexible Öffnungszeiten
                </div>
              </div>
              <div style={{ background: DARK_GRAY, padding: 22, borderLeft: `4px solid ${YELLOW}`, borderRadius: 8 }}>
                <div style={{ fontSize: 'clamp(1.8rem, 9vw, 3rem)', fontWeight: 'bold', color: YELLOW, marginBottom: 6 }}>100%</div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Professionell
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- ABOUT ---------- */}
        <section
          id="uber-uns"
          ref={aboutRef}
          style={{ background: '#f5f1e8', padding: '90px 20px', opacity: 0, transform: 'translateY(50px)', transition: 'all 0.8s ease-out' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="about-content">
              <div style={{ animation: 'slideInLeft 0.8s ease-out' }}>
                <h2 style={{ fontSize: 'clamp(1.8rem, 5.5vw, 3.2rem)', fontWeight: 'bold', color: BLACK, marginBottom: 16, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                  Über <span style={{ color: YELLOW }}>Uns</span>
                </h2>
                <div style={{ width: 80, height: 4, backgroundColor: YELLOW, marginBottom: 22 }} />
                <p style={{ fontSize: '1.05rem', color: '#4a4a4a', lineHeight: 1.75, fontWeight: 400, marginBottom: 18 }}>
                  Willkommen in der Auto Hobby Werkstatt – dem Ort, wo deine automobilen Träume Wirklichkeit werden. Ob Restaurierung, Tuning
                  oder einfache Wartung: Bei uns findest du den perfekten Raum und die passende Ausstattung.
                </p>
                <p style={{ fontSize: '1.05rem', color: '#4a4a4a', lineHeight: 1.75, fontWeight: 400 }}>
                  Unsere moderne Halle bietet alles, was du für professionelles Arbeiten brauchst. Hebebühnen, Werkzeuge, Platz und Know-how –
                  alles unter einem Dach.
                </p>
              </div>
              <div style={{ animation: 'slideInRight 0.8s ease-out' }}>
                <img
                  src="/1 (3).jpeg"
                  alt="Werkstatt Innen"
                  style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', transition: 'all 0.4s ease' }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ---------- HIGHLIGHTS ---------- */}
        <section
          ref={highlightsRef}
          style={{ background: WHITE, padding: '70px 20px', opacity: 0, transform: 'translateY(50px)', transition: 'all 0.8s ease-out' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4.5vw, 2.5rem)', fontWeight: 900, color: BLACK, marginBottom: 36, letterSpacing: '-1px' }}>
              Deine <span style={{ color: YELLOW }}>Vorteile</span>
            </h2>

            <div className="highlights-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {highlights.map((item, i) => (
                <div
                  key={i}
                  style={{
                    background: '#f8f8f8',
                    padding: 22,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    borderLeft: `4px solid ${i % 2 === 0 ? YELLOW : 'transparent'}`,
                    borderRight: `4px solid ${i % 2 === 1 ? YELLOW : 'transparent'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.transform = 'translateX(8px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f8f8f8'; e.currentTarget.style.transform = 'translateX(0)'; }}
                >
                  <div style={{ width: 60, height: 60, flexShrink: 0, background: YELLOW, display: 'flex', alignItems: 'center', justifyContent: 'center', color: BLACK }}>
                    {item.icon === 'workspace' && (
                      <svg style={{ width: 28, height: 28, stroke: 'currentColor', fill: 'none', strokeWidth: 2 }} viewBox="0 0 24 24">
                        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
                      </svg>
                    )}
                    {item.icon === 'tools' && (
                      <svg style={{ width: 28, height: 28, stroke: 'currentColor', fill: 'none', strokeWidth: 2 }} viewBox="0 0 24 24">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                      </svg>
                    )}
                    {item.icon === 'clean' && (
                      <svg style={{ width: 28, height: 28, stroke: 'currentColor', fill: 'none', strokeWidth: 2 }} viewBox="0 0 24 24">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    )}
                    {item.icon === 'time' && (
                      <svg style={{ width: 28, height: 28, stroke: 'currentColor', fill: 'none', strokeWidth: 2 }} viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                    )}
                    {item.icon === 'badge-check' && (
                      <svg style={{ width: 28, height: 28 }} stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
                      </svg>
                    )}
                    {item.icon === 'sparkles' && (
                      <svg style={{ width: 28, height: 28 }} stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 2v4m0 12v4m10-10h-4M6 12H2m14.1 5.9L19 22l-4.1-4.1M5 5l4.1 4.1L5 13" />
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: BLACK, marginBottom: 6, letterSpacing: '-0.5px' }}>{item.title}</h3>
                    <p style={{ fontSize: '0.95rem', color: '#666', lineHeight: 1.5, fontWeight: 400 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- GALLERY (6 images) ---------- */}
        <section
          ref={galleryRef}
          style={{ background: BLACK, padding: '70px 20px', opacity: 0, transform: 'translateY(50px)', transition: 'all 0.8s ease-out' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="gallery-header-section">
              <div>
                <h2 style={{ fontSize: 'clamp(1.6rem, 4.5vw, 2.5rem)', fontWeight: 900, color: WHITE, marginBottom: 10, letterSpacing: '-1px' }}>
                  Unsere <span style={{ color: YELLOW }}>Halle</span>
                </h2>
                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.75)', maxWidth: 600, lineHeight: 1.5 }}>
                  Moderne Ausstattung, professionelle Arbeitsplätze und alles, was du für dein Projekt brauchst – an einem Ort.
                </p>
              </div>
              
              <Link to="/galerie" className="view-gallery-btn">
                <span>Vollständige Galerie ansehen</span>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <div className="gallery-grid">
              {galleryImages.map((g, i) => (
                <div key={i} className={`gallery-item ${g.span2 ? 'span-2' : ''} ${g.tall ? 'h-320' : 'h-280'}`}>
                  <img src={g.src} alt={g.title} className="img" />
                  <div className="gallery-caption">
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 'bold', marginBottom: 4 }}>{g.title}</h3>
                    <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>{g.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}